/**
 * S4 — 402 → Upgrade-Prompt Modal
 *
 * Triggered when user tries to access a gated feature.
 * Three variants:
 *   feature_not_enabled (lock): "AI Scribe is a premium feature" → "View plans"/"Notify admin"
 *   trial_expired (hourglass):  "Your AI Scribe trial has ended"  → "Upgrade now"/"Notify admin"
 *   subscription_expired:       "Your AI Scribe subscription has ended" → "Upgrade now"/"Notify admin"
 *
 * Max-w 480, ghost background.
 *
 * Figma: S4 — 402 → Upgrade-Prompt Modal
 */
import React from "react";

export type UpgradeVariant = "feature_not_enabled" | "trial_expired" | "subscription_expired";

interface UpgradePromptModalProps {
  feature: string;
  featureLabel: string;
  variant: UpgradeVariant;
  expiredDate?: string | null;
  isAdmin: boolean;
  onDismiss: () => void;
  onUpgradeClick: () => void;
  onNotifyAdmin: () => void;
}

export const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  featureLabel,
  variant,
  expiredDate,
  isAdmin,
  onDismiss,
  onUpgradeClick,
  onNotifyAdmin,
}) => {
  const isExpired = variant === "trial_expired" || variant === "subscription_expired";
  const isTrial = variant === "trial_expired";

  const handleAction = () => {
    onDismiss();
    if (isAdmin) onUpgradeClick();
    else onNotifyAdmin();
  };

  const formattedDate = expiredDate
    ? new Date(expiredDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  // Title text based on variant
  const title = isTrial
    ? `Your ${featureLabel} trial has ended`
    : variant === "subscription_expired"
      ? `Your ${featureLabel} subscription has ended`
      : `${featureLabel} is a premium feature`;

  // Subtitle text based on variant
  const subtitle = isTrial
    ? `Your ${featureLabel} trial ended${formattedDate ? ` on ${formattedDate}` : ""}.`
    : variant === "subscription_expired"
      ? `Your ${featureLabel} subscription ended${formattedDate ? ` on ${formattedDate}` : ""}. Renew to continue using this feature.`
      : `Upgrade your plan to enable ${featureLabel} for your clinic.`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[480px] w-full p-5 sm:p-8 text-center">
        {/* Icon */}
        <div className="mx-auto mb-3 sm:mb-4 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-100 flex items-center justify-center">
          {isExpired ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M17 2v4.172a2 2 0 0 1-.586 1.414L12 12l-4.414-4.414A2 2 0 0 1 7 6.172V2" />
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          )}
        </div>

        {/* Title */}
        <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-1.5 sm:mb-2">{title}</h2>

        {/* Subtitle */}
        <p className="text-xs sm:text-sm text-slate-500 mb-5 sm:mb-6">{subtitle}</p>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
          <button
            onClick={onDismiss}
            className="w-full sm:w-auto px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors order-2 sm:order-1"
          >
            Dismiss
          </button>
          <button
            onClick={handleAction}
            className="w-full sm:w-auto px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors order-1 sm:order-2"
          >
            {isAdmin
              ? (isExpired ? "Upgrade now" : "View plans")
              : "Notify admin"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePromptModal;
